import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useState } from 'react';
import { Eye } from '@phosphor-icons/react';
import { Box } from '@mui/material';

import {
  Form,
  Header,
  InternalPage,
  ResetFormHandler,
  Spacer,
  IconButton,
} from 'theSource/components';
import {
  CHANGE_PASSWORD_FORM_INITIAL_VALUES,
  CHANGE_PASSWORD_QUERY_KEY,
  RESET_FORM_ERROR_CODES,
} from './utils';
import { useApiMutation, newPasswordIsValid } from 'theSource/utils';
import {
  ChangePasswordErrorDto,
  ChangePasswordFormValues,
  ChangePasswordRequestDto,
  ChangePasswordResponseDto,
} from './types';
import { setAuthenticated, PasswordRequirements } from 'theSource/features';
import { setIsPasswordChanged } from './services';
import {
  GET_CONTENT_SETTINGS_QUERY_KEY,
  usePasswordRequirementsQuery,
  useConfigurationQuery,
  useCompanyManifestQuery,
} from 'theSource/queries';
import { ROUTES } from 'theSource/app/routes';
import {
  ChangePasswordWrapperStyled,
  TextFieldStyled,
} from './ChangePasswordStyled';
import { mapChangePasswordData } from './utils/mappingUtils';
import { FormValues } from 'theSource/types';

export const LumosChangePassword = () => {
  const { t } = useTranslation('pages.changePassword');
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const { isLoading, mutate: changePassword } = useApiMutation<
    ChangePasswordResponseDto,
    ChangePasswordErrorDto,
    ChangePasswordRequestDto
  >({
    queryKey: CHANGE_PASSWORD_QUERY_KEY,
    method: 'put',
    endpoint: '/password_change',
    queryKeysToInvalidate: GET_CONTENT_SETTINGS_QUERY_KEY,
    onSuccess: () => {
      dispatch(setIsPasswordChanged(true));
      dispatch(setAuthenticated(true));
      navigate(ROUTES.main.home.path);
    },
    getErrorMessage: (error) => error.response?.data.message,
    showSuccessSnackbar: false,
    snackbarOptions: {
      position: 'bottom',
    },
  });

  const handleSubmit = (
    values: ChangePasswordFormValues,
    resetForm: ResetFormHandler
  ) => {
    changePassword(mapChangePasswordData(values), {
      onError: (error) => {
        const errorCode = error.response?.data.error_code;
        if (errorCode && RESET_FORM_ERROR_CODES.includes(errorCode)) {
          resetForm();
        }
      },
    });
  };

  const { configuration } = useConfigurationQuery();
  const baseUrl = configuration?.apiHost;

  const { data: manifestData, isLoading: isLoadingManifestData } =
    useCompanyManifestQuery({ enabled: Boolean(configuration?.apiHost) });

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  const {
    requirements,
    isLoadingPasswordRequirements,
    isFetchingPasswordRequirements,
  } = usePasswordRequirementsQuery(
    newPassword,
    '',
    manifestData?.organization_id,
    baseUrl
  );

  const isLoadingRequirements =
    isLoadingManifestData ||
    isLoadingPasswordRequirements ||
    isFetchingPasswordRequirements;

  const changeNewPassword = (values: FormValues) => {
    const newPassword = values.newPassword as string;
    const confirmPassword = values.reEnterPassword as string;
    setNewPassword(newPassword);
    setConfirmPassword(confirmPassword);
  };

  const [displayNewPassword, setDisplayNewPassword] = useState(false);
  const [displayConfirmPassword, setDisplayConfirmPassword] = useState(false);

  const handleDisplayNewPassword = () => {
    setDisplayNewPassword(!displayNewPassword);
  };

  const handleDisplayConfirmPassword = () => {
    setDisplayConfirmPassword(!displayConfirmPassword);
  };

  const newPasswordValid = newPasswordIsValid(newPassword, requirements);
  const formValid = newPasswordValid && newPassword === confirmPassword;

  return (
    <Box data-testid="change-password-page">
      <Header hideDrawer />
      <ChangePasswordWrapperStyled>
        <InternalPage title={t('title')} description={t('description')}>
          <div className="mt-2">
            <p>
              <span className="red">*</span> = ({t('common:required')})
            </p>
          </div>
          <Form<ChangePasswordFormValues>
            onSubmitClick={handleSubmit}
            initialValues={CHANGE_PASSWORD_FORM_INITIAL_VALUES}
            isLoading={isLoading}
            submitButtonLabel={t('submitButtonLabel')}
            submitButtonUseFullWidth
            onChange={changeNewPassword}
            disableSubmitButton={!formValid}
          >
            <TextFieldStyled
              required
              label={t('newPassword')}
              type={displayNewPassword ? 'text' : 'password'}
              id="newPassword"
              testId="newPassword"
              name="newPassword"
              endAdornment={
                <IconButton
                  Icon={Eye}
                  size={3.0}
                  aria-label="toggle-display-new-password"
                  onClick={handleDisplayNewPassword}
                  variant="tertiary"
                />
              }
            />
            <PasswordRequirements
              requirements={requirements}
              loadingRequirements={isLoadingRequirements}
              password={newPassword}
            />
            <TextFieldStyled
              required
              label={t('reEnterPassword')}
              type={displayConfirmPassword ? 'text' : 'password'}
              id="reEnterPassword"
              testId="reEnterPassword"
              name="reEnterPassword"
              disabled={!newPasswordValid}
              endAdornment={
                <IconButton
                  Icon={Eye}
                  size={3.0}
                  aria-label="toggle-display-new-password"
                  onClick={handleDisplayConfirmPassword}
                  variant="tertiary"
                />
              }
            />
            <Spacer />
          </Form>
        </InternalPage>
      </ChangePasswordWrapperStyled>
    </Box>
  );
};
